import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { utc } from '@date-fns/utc'
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    EditOutlined,
    TodayOutlined,
} from '@mui/icons-material'
import { Box, Grow } from '@mui/material'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { addWeeks, formatISO, startOfWeek, subWeeks } from 'date-fns'
import { User } from 'oidc-client-ts'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { z } from 'zod/v4-mini'
import { FabLink } from '@/components/FabLink'
import { IconButtonLink } from '@/components/IconButtonLink'
import { SearchField } from '@/components/SearchField'
import { TrackingsCalendar } from '@/features/trackings/components/TrackingsCalendar'
import { TrackingsCalendarPendingComponent } from '@/features/trackings/components/TrackingsCalendarPendingComponent'
import { TrackingsFilters } from '@/features/trackings/components/TrackingsFilters'
import { TrackingsProjectFilterChip } from '@/features/trackings/components/TrackingsProjectFilterChip'
import { createTrackingSearching } from '@/features/trackings/helper/createTrackingsSearching'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { TrackingSortBy } from '@/generated/Enumerations/TrackingSortBy_pb'
import { TrackingFiltersDto } from '@/generated/Messages/Filters_pb'
import { debounce } from '@/helper/debounce'
import { parseId, stringifyId } from '@/helper/id'
import { useInterval } from '@/hooks/useInterval'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import { trackingsQueryOptions, useTrackings } from '@/queries/useTrackings'
import {
    searchParamsProjectIds,
    searchParamsProjectIdsDefaultValue,
} from '@/schemas/searchParams/searchParamsProjectIds'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'trackings-calendar'

const queryFilters = (
    searchText: string,
    user?: User | null,
    weekStart?: string,
    projects?: string[]
): Partial<OmitDeepMessageFields<TrackingFiltersDto>> => {
    // weekStart should already be the first day of the week.
    // But just in case, calculate a new start of the week based on the old one.
    const actualWeekStart = startOfWeek(
        weekStart ? new Date(weekStart) : new Date(),
        { in: utc }
    )

    return {
        sorting: [{ sortBy: TrackingSortBy.STARTED_AT, descending: true }],
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
        searching: createTrackingSearching(searchText),
        userIds: user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
        dateTimeFrom: timestampFromDate(actualWeekStart),
        dateTimeTo: timestampFromDate(addWeeks(actualWeekStart, 1)),
        projectIds: projects ? projects.map(parseId) : undefined,
    }
}

const CalendarNavigation: React.FC = () => (
    <div>
        <IconButtonLink
            size={'small'}
            to={'.'}
            replace={true}
            search={prev => ({
                ...prev,
                weekStart: formatISO(
                    subWeeks(
                        startOfWeek(
                            prev.weekStart ?
                                new Date(prev.weekStart)
                            :   new Date(),
                            { in: utc }
                        ),
                        1
                    ),
                    { representation: 'date' }
                ),
            })}
        >
            <ArrowLeftOutlined />
        </IconButtonLink>
        <IconButtonLink
            size={'small'}
            to={'.'}
            replace={true}
            search={prev => ({ ...prev, weekStart: undefined })}
        >
            <TodayOutlined />
        </IconButtonLink>
        <IconButtonLink
            size={'small'}
            to={'.'}
            replace={true}
            search={prev => ({
                ...prev,
                weekStart: formatISO(
                    addWeeks(
                        startOfWeek(
                            prev.weekStart ?
                                new Date(prev.weekStart)
                            :   new Date(),
                            { in: utc }
                        ),
                        1
                    ),
                    { representation: 'date' }
                ),
            })}
            sx={{ marginRight: 2 }}
        >
            <ArrowRightOutlined />
        </IconButtonLink>
    </div>
)

const RouteComponent: React.FC = () => {
    useInterval(60 * 1000)

    const { searchText, weekStart, projectIds } = Route.useSearch()

    const navigate = Route.useNavigate()
    const { t } = useTranslation()

    const [localSearchText, setLocalSearchText] = React.useState(searchText)

    const { user } = useAuth()

    const {
        data: { trackings },
    } = useTrackings(queryFilters(searchText, user, weekStart, projectIds))
    const {
        data: { projects },
    } = useProjects({
        userIds: user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
        sorting: [{ sortBy: ProjectSortBy.NAME }],
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedNavigate = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText }),
            })
        }),
        []
    )

    return (
        <>
            <title>{t('common.trackings')}</title>

            <SearchField
                fullWidth={true}
                value={localSearchText}
                onChange={({ target: { value } }) => {
                    setLocalSearchText(value)

                    // Change search params only after the timeout has finished.
                    debouncedNavigate(value)
                }}
                onClickReset={async () => {
                    setLocalSearchText('')

                    await navigate({
                        search: prev => ({ ...prev, searchText: '' }),
                    })
                }}
                data-selenium={`${seleniumPrefix}-search-field`}
            />

            <Box
                sx={{
                    marginTop: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <CalendarNavigation />

                <TrackingsFilters
                    // Fixes state issues, because this way the state gets reset when from, to or projectIds changes.
                    key={JSON.stringify(projectIds)}
                    variant={'calendar'}
                    projects={projectIds}
                    availableProjects={projects}
                    handleSubmit={async ({ projects }) => {
                        await navigate({
                            to: '.',
                            search: state => ({
                                ...state,
                                projectIds: projects,
                            }),
                        })
                    }}
                />

                {projectIds.flatMap(pId => {
                    const project = projects.find(
                        p => stringifyId(p.id) === pId
                    )

                    return project ?
                            [
                                <TrackingsProjectFilterChip
                                    key={pId}
                                    projectName={project.name}
                                    onDelete={() =>
                                        navigate({
                                            to: '.',
                                            search: prev => ({
                                                ...prev,
                                                projectIds:
                                                    prev.projectIds.filter(
                                                        ppId => ppId !== pId
                                                    ),
                                            }),
                                        })
                                    }
                                />,
                            ]
                        :   []
                })}
            </Box>

            <TrackingsCalendar
                trackings={trackings}
                projects={projects}
                start={startOfWeek(
                    weekStart ? new Date(weekStart) : new Date(),
                    {
                        in: utc,
                    }
                )}
            />

            <Grow in={true}>
                <FabLink
                    variant={'extended'}
                    sx={theme => ({
                        position: 'absolute',
                        bottom:
                            user?.profile.role === 'Administrator' ?
                                theme.spacing(2)
                            :   { xs: theme.spacing(9), sm: theme.spacing(2) },
                        right: theme.spacing(2),
                    })}
                    color={'primary'}
                    to={'.'}
                    search={prev => ({ ...prev, showEditTrackings: true })}
                    data-selenium={`${seleniumPrefix}-edit`}
                >
                    <EditOutlined /> {t('common.edit')}
                </FabLink>
            </Grow>
        </>
    )
}

const PendingComponent: React.FC = () => {
    const { searchText, weekStart, projectIds } = Route.useSearch()

    return (
        <TrackingsCalendarPendingComponent
            searchText={searchText}
            start={startOfWeek(weekStart ? new Date(weekStart) : new Date(), {
                in: utc,
            })}
            projectIds={projectIds}
        />
    )
}

export const Route = createFileRoute(
    '/__base-layout/__edit-trackings/trackings/calendar'
)({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsSearchTextSchema.shape,
        ...searchParamsProjectIds.shape,

        weekStart: z.catch(z.optional(z.iso.date()), undefined),
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsSearchTextDefaultValues,
                ...searchParamsProjectIdsDefaultValue,
            }),
        ],
    },

    pendingComponent: PendingComponent,

    beforeLoad: async ({
        context: { queryClient, grpcClients, user },
        search: { searchText, weekStart, projectIds },
    }) => {
        await Promise.all([
            queryClient.ensureQueryData(
                trackingsQueryOptions(
                    grpcClients,
                    queryFilters(searchText, user, weekStart, projectIds)
                )
            ),
            queryClient.ensureQueryData(
                projectsQueryOptions(grpcClients, {
                    userIds:
                        user?.profile.sub ?
                            [parseId(user.profile.sub)]
                        :   undefined,
                    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
                    sorting: [{ sortBy: ProjectSortBy.NAME }],
                })
            ),
        ])
    },
})
